import { supabase } from "./supabase";
import { type RegimeFiscalDossier } from "./dossierFiscalite";

const REGIMES_DOSSIER_CHECK = new Set<string>(["rni", "rsi", "micro", "tee"]);

/** Assure une valeur acceptée par `dossiers_regime_fiscal_check`. */
export function normaliserRegimeFiscalDossier(v: string | null | undefined): RegimeFiscalDossier {
  const t = (v ?? "").trim().toLowerCase();
  return REGIMES_DOSSIER_CHECK.has(t) ? (t as RegimeFiscalDossier) : "rni";
}

export interface Dossier {
  id: string;
  nom: string;
  cabinet_id: string;
  pays?: "CI" | "SN" | "CM" | null;
  regime_fiscal?: string | null;
  date_creation?: string | null;
  date_cloture_exercice?: string | null;
  exercice_long?: boolean | null;
  type_comptabilite?: "tresorerie" | "engagement" | null;
  created_at?: string;
}

export type CreateDossierInput = {
  nom: string;
  pays?: "CI" | "SN" | "CM" | null;
  regime_fiscal?: string | null;
  date_creation?: string | null;
  date_cloture_exercice?: string | null;
  exercice_long?: boolean;
  type_comptabilite?: "tresorerie" | "engagement" | null;
};

/** Récupère les dossiers du cabinet de l'utilisateur connecté */
export async function fetchDossiers(): Promise<Dossier[]> {
  const { data, error } = await supabase
    .from("dossiers")
    .select("id, nom, cabinet_id, created_at, type_comptabilite, regime_fiscal")
    .order("nom");

  if (error) throw error;
  return data || [];
}

/** Crée un nouveau dossier */
export async function createDossier(input: string | CreateDossierInput): Promise<Dossier> {
  const payload =
    typeof input === "string"
      ? { nom: input }
      : {
          nom: input.nom,
          pays: input.pays ?? null,
          regime_fiscal: normaliserRegimeFiscalDossier(input.regime_fiscal),
          date_creation: input.date_creation ?? null,
          date_cloture_exercice: input.date_cloture_exercice ?? null,
          exercice_long: input.exercice_long ?? false,
          type_comptabilite: input.type_comptabilite ?? null,
        };

  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) throw new Error("Non connecté");

  const { data: cabinet } = await supabase
    .from("cabinets")
    .select("id")
    .eq("user_id", userData.user.id)
    .single();

  if (!cabinet) {
    const { data: newCabinet, error: ecErr } = await supabase
      .from("cabinets")
      .insert({ user_id: userData.user.id, nom: userData.user.user_metadata?.cabinet_name || "Mon cabinet" })
      .select("id")
      .single();
    if (ecErr || !newCabinet) throw new Error("Impossible de créer le cabinet");
    const { data: d, error } = await supabase
      .from("dossiers")
      .insert({ cabinet_id: newCabinet.id, ...payload })
      .select()
      .single();
    if (error) throw error;
    await supabase.from("dossiers_users").upsert(
      { dossier_id: d.id, user_id: userData.user.id },
      { onConflict: "dossier_id,user_id" }
    );
    return d;
  }

  const { data, error } = await supabase
    .from("dossiers")
    .insert({ cabinet_id: cabinet.id, ...payload })
    .select()
    .single();

  if (error) throw error;
  await supabase.from("dossiers_users").upsert(
    { dossier_id: data.id, user_id: userData.user.id },
    { onConflict: "dossier_id,user_id" }
  );
  return data;
}

/** Vérifie si l'utilisateur est propriétaire d'un cabinet (exclut les collaborateurs) */
export async function isCabinetOwner(): Promise<boolean> {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) return false;
  const { data: owns } = await supabase.from("cabinets").select("id").eq("user_id", userData.user.id).maybeSingle();
  return !!owns;
}

/** Vérifie si l'utilisateur possède un cabinet ou est collaborateur d'un cabinet */
export async function hasCabinet(): Promise<boolean> {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) return false;
  const { data: owns } = await supabase.from("cabinets").select("id").eq("user_id", userData.user.id).maybeSingle();
  if (owns) return true;
  const { data: collab } = await supabase.from("cabinet_employes").select("id").eq("user_id", userData.user.id).limit(1);
  return !!(collab && collab.length > 0);
}

/** Récupère les dossiers auxquels le client a accès (via invitation) */
export async function fetchClientDossiers(): Promise<Dossier[]> {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) return [];

  const { data, error } = await supabase
    .from("dossiers_users")
    .select("dossier_id, dossiers(id, nom, cabinet_id, created_at, type_comptabilite, regime_fiscal)")
    .eq("user_id", userData.user.id);

  if (error || !data || data.length === 0) return [];

  const dossiers = data
    .flatMap((r) => {
      const d = (r as { dossier_id?: string; dossiers?: Dossier | Dossier[] | null }).dossiers;
      if (d == null) return [];
      if (Array.isArray(d)) {
        if (d.length === 0) return [];
        return d.filter((x): x is Dossier => x != null);
      }
      return [d];
    })
    .filter((d): d is Dossier => d != null);

  return dossiers.sort((a, b) => (a.nom || "").localeCompare(b.nom || ""));
}

/** Dossiers où l'utilisateur courant est membre (dossier_membres), avec le nom depuis `dossiers` */
export async function fetchDossiersMembre(): Promise<{
  id: string;
  nom: string;
  regime_fiscal?: string | null;
  type_comptabilite?: "tresorerie" | "engagement" | null;
}[]> {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) return [];

  const { data, error } = await supabase
    .from("dossier_membres")
    .select("dossier_id, dossiers(id, nom, regime_fiscal, type_comptabilite)")
    .eq("user_id", userData.user.id);

  if (error || !data?.length) return [];

  /** Ligne jointe Supabase : `dossiers` peut être un objet, un tableau ou null (RLS). */
  type DossierJoin = {
    id: string;
    nom: string | null;
    regime_fiscal?: string | null;
    type_comptabilite?: "tresorerie" | "engagement" | null;
  };
  type DossierMembreRow = {
    dossier_id: string;
    dossiers: DossierJoin | DossierJoin[] | null;
  };

  const byId = new Map<
    string,
    { id: string; nom: string; regime_fiscal?: string | null; type_comptabilite?: "tresorerie" | "engagement" | null }
  >();
  const rows = data as unknown as DossierMembreRow[];
  for (const row of rows) {
    const raw = row.dossiers;
    const joined: DossierJoin | null = Array.isArray(raw) ? (raw[0] ?? null) : raw;
    const id = joined?.id ?? row.dossier_id;
    if (id && !byId.has(id)) {
      // Si la RLS sur `dossiers` masque le join, on garde au moins dossier_id (collaborateur)
      const nom = joined?.nom?.trim() || "Dossier";
      byId.set(id, {
        id,
        nom,
        regime_fiscal: joined?.regime_fiscal ?? null,
        type_comptabilite: joined?.type_comptabilite ?? null,
      });
    }
  }

  return [...byId.values()].sort((a, b) => (a.nom || "").localeCompare(b.nom || ""));
}

/** Ligne unique pour sélecteur (mobile / picker) : union client + cabinet + membre. */
export type DossierAccessibleRow = {
  id: string;
  nom: string;
  regime_fiscal?: string | null;
  type_comptabilite?: "tresorerie" | "engagement" | null;
};

function mergeDossierAccessibleLists(lists: DossierAccessibleRow[][]): DossierAccessibleRow[] {
  const m = new Map<string, DossierAccessibleRow>();
  for (const list of lists) {
    for (const d of list) {
      if (d?.id && !m.has(d.id)) {
        m.set(d.id, { ...d });
      }
    }
  }
  return [...m.values()].sort((a, b) => (a.nom || "").localeCompare(b.nom || "", "fr"));
}

/** Tous les dossiers accessibles à l’utilisateur connecté (même logique que Layout). */
export async function fetchAllDossiersAccessibles(): Promise<DossierAccessibleRow[]> {
  const [client, cabinet, membre] = await Promise.all([
    fetchClientDossiers().catch(() => [] as Dossier[]),
    fetchDossiers().catch(() => [] as Dossier[]),
    fetchDossiersMembre().catch(() => [] as Awaited<ReturnType<typeof fetchDossiersMembre>>),
  ]);
  const toRow = (d: Dossier | (typeof membre)[number]): DossierAccessibleRow => ({
    id: d.id,
    nom: (d.nom || "Dossier").trim(),
    regime_fiscal: "regime_fiscal" in d ? d.regime_fiscal ?? null : null,
    type_comptabilite: "type_comptabilite" in d ? d.type_comptabilite ?? null : null,
  });
  return mergeDossierAccessibleLists([
    client.map(toRow),
    cabinet.map(toRow),
    membre.map(toRow),
  ]);
}

// ─── Dossiers infos (IDU / Certificat d'immatriculation) ───────────

export interface DossierInfos {
  dossier_id: string;
  niu?: string | null;
  denomination?: string | null;
  forme_juridique?: string | null;
  capital_social?: number | null;
  date_requisition?: string | null;
  siege_social?: string | null;
  adresse_postale?: string | null;
  ville?: string | null;
  telephone?: string | null;
  email?: string | null;
  activites_principales?: string | null;
  date_debut_activite?: string | null;
  rc?: string | null;
  centre_impots?: string | null;
  agence_cnps?: string | null;
  dirigeant_nom?: string | null;
  dirigeant_qualite?: string | null;
  /** FNE : Numéro Compte Contribuable */
  ncc?: string | null;
  regime_imposition?: string | null;
  rccm?: string | null;
  references_bancaires?: string | null;
  vendeur_nom?: string | null;
  point_vente?: string | null;
  formule_politesse?: string | null;
  phrase_bas_page?: string | null;
  logo_url?: string | null;
  logo_fne_url?: string | null;
  /** Liasse fiscale SYSCOHADA (NINEA / NIF selon le pays) */
  ninea?: string | null;
  sigle?: string | null;
  greffe?: string | null;
  /** Caisse sociale (CNPS, CSS, etc.) */
  numero_cnps?: string | null;
  code_importateur?: string | null;
  activite_principale_code?: string | null;
  contact_nom?: string | null;
  contact_qualite?: string | null;
  contact_telephone?: string | null;
  expert_comptable_nom?: string | null;
  expert_comptable_adresse?: string | null;
  expert_comptable_telephone?: string | null;
  expert_comptable_ordre?: string | null;
  note2_derogations?: string | null;
  note2_infos_complementaires?: string | null;
}

/** Régimes d'imposition CI (RNI, TEE, etc.) */
export const REGIME_IMPOSITION_OPTIONS = [
  { value: "RNI", label: "RNI - Régime du Revenu Net Imposé" },
  { value: "TEE", label: "TEE - Taxe sur les Eaux Énergétiques" },
  { value: "RS", label: "RS - Régime Simplifié" },
  { value: "CF", label: "CF - Contribution Forfaitaire" },
  { value: "Autre", label: "Autre" },
];

/** Récupère les infos client d'un dossier */
export async function fetchDossierInfos(dossierId: string): Promise<DossierInfos | null> {
  const { data, error } = await supabase
    .from("dossiers_infos")
    .select("*")
    .eq("dossier_id", dossierId)
    .maybeSingle();
  if (error) throw error;
  return data;
}

/** Sauvegarde ou met à jour les infos client d'un dossier */
export async function saveDossierInfos(
  dossierId: string,
  infos: Partial<Omit<DossierInfos, "dossier_id">>
): Promise<DossierInfos> {
  const payload = { dossier_id: dossierId, ...infos };
  const { data, error } = await supabase
    .from("dossiers_infos")
    .upsert(payload, { onConflict: "dossier_id" })
    .select()
    .single();
  if (error) throw error;
  return data;
}

/** Options pour les champs select du formulaire IDU */
export const FORME_JURIDIQUE_OPTIONS = [
  { value: "EI", label: "EI - Entreprise Individuelle" },
  { value: "EURL", label: "EURL" },
  { value: "SARL", label: "SARL" },
  { value: "SA", label: "SA" },
  { value: "SAS", label: "SAS" },
  { value: "GIE", label: "GIE" },
  { value: "Autre", label: "Autre" },
];

export const DIRIGEANT_QUALITE_OPTIONS = [
  { value: "GERANT", label: "Gérant" },
  { value: "PRESIDENT", label: "Président" },
  { value: "DG", label: "Directeur Général" },
  { value: "ASSOCIE", label: "Associé" },
  { value: "Autre", label: "Autre" },
];

/** Centres des Impôts Côte d'Ivoire (groupés) */
export const CENTRE_IMPOTS_OPTIONS: { value: string; label: string; group: string }[] = [
  { value: "DGE", label: "DGE - Direction des Grandes Entreprises", group: "Directions Nationales" },
  { value: "DME_NORD", label: "DME - Abidjan Nord (Moyennes Entreprises)", group: "Directions Nationales" },
  { value: "DME_SUD", label: "DME - Abidjan Sud (Moyennes Entreprises)", group: "Directions Nationales" },
  { value: "CDI_PLATEAU_1", label: "CDI Plateau 1", group: "Abidjan Sud" },
  { value: "CDI_PLATEAU_2", label: "CDI Plateau 2", group: "Abidjan Sud" },
  { value: "CDI_TREICHVILLE_1", label: "CDI Treichville 1", group: "Abidjan Sud" },
  { value: "CDI_TREICHVILLE_2", label: "CDI Treichville 2", group: "Abidjan Sud" },
  { value: "CDI_MARCORY_1", label: "CDI Marcory 1", group: "Abidjan Sud" },
  { value: "CDI_MARCORY_2", label: "CDI Marcory 2", group: "Abidjan Sud" },
  { value: "CDI_KOUMASSI_1", label: "CDI Koumassi 1", group: "Abidjan Sud" },
  { value: "CDI_KOUMASSI_2", label: "CDI Koumassi 2", group: "Abidjan Sud" },
  { value: "CDI_PORT_BOUET_1", label: "CDI Port-Bouët 1", group: "Abidjan Sud" },
  { value: "CDI_PORT_BOUET_2", label: "CDI Port-Bouët 2", group: "Abidjan Sud" },
  { value: "CDI_COCODY_1", label: "CDI Cocody 1 (Angré)", group: "Abidjan Nord" },
  { value: "CDI_COCODY_2", label: "CDI Cocody 2 (Riviera)", group: "Abidjan Nord" },
  { value: "CDI_DEUX_PLATEAUX", label: "CDI Deux-Plateaux", group: "Abidjan Nord" },
  { value: "CDI_ADJAME_1", label: "CDI Adjamé 1", group: "Abidjan Nord" },
  { value: "CDI_ADJAME_2", label: "CDI Adjamé 2", group: "Abidjan Nord" },
  { value: "CDI_ABOBO_1", label: "CDI Abobo 1", group: "Abidjan Nord" },
  { value: "CDI_ABOBO_2", label: "CDI Abobo 2", group: "Abidjan Nord" },
  { value: "CDI_YOPOUGON_1", label: "CDI Yopougon 1 (Andokoi)", group: "Abidjan Nord" },
  { value: "CDI_YOPOUGON_2", label: "CDI Yopougon 2 (Niangon)", group: "Abidjan Nord" },
  { value: "CDI_YOPOUGON_3", label: "CDI Yopougon 3 (Zone Industrielle)", group: "Abidjan Nord" },
  { value: "CDI_ANYAMA", label: "CDI Anyama", group: "Abidjan Nord" },
  { value: "CDI_BINGERVILLE", label: "CDI Bingerville", group: "Abidjan Nord" },
  { value: "CDI_YAMOUSSOUKRO", label: "CDI Yamoussoukro", group: "Intérieur" },
  { value: "CDI_BOUAKE_1", label: "CDI Bouaké 1", group: "Intérieur" },
  { value: "CDI_BOUAKE_2", label: "CDI Bouaké 2", group: "Intérieur" },
  { value: "CDI_SAN_PEDRO_1", label: "CDI San-Pédro 1", group: "Intérieur" },
  { value: "CDI_SAN_PEDRO_2", label: "CDI San-Pédro 2", group: "Intérieur" },
  { value: "CDI_DALOA", label: "CDI Daloa", group: "Intérieur" },
  { value: "CDI_KORHOGO", label: "CDI Korhogo", group: "Intérieur" },
  { value: "CDI_MAN", label: "CDI Man", group: "Intérieur" },
  { value: "CDI_GAGNOA", label: "CDI Gagnoa", group: "Intérieur" },
  { value: "CDI_ABOISSO", label: "CDI Aboisso", group: "Intérieur" },
  { value: "CDI_SINFRA", label: "CDI Sinfra", group: "Intérieur" },
  { value: "CDI_SOUBRE", label: "CDI Soubré", group: "Intérieur" },
  { value: "CDI_AGBOVILLE", label: "CDI Agboville", group: "Intérieur" },
];
